import {
  getList,
  getString,
  getOptionalString,
  getLeoDate,
  LeoDate,
  getOptionalLeoDate,
  getObject,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { GetProjectBOUsersSortOrder, GetProjectBOUsersSortOrderDTO, GetProjectBOUsersSortOrderEnums } from "./getProjectBoUsersSortOrder";
import { ProjectItemsPerPage, ProjectItemsPerPageDTO } from "./projectItemsPerPage";
import { ProjectPageIndex, ProjectPageIndexDTO } from "./projectPageIndex";
import { GetProjectBOUsersPaginationResponse, GetProjectBOUsersPaginationResponseDTO } from "./getProjectBoUsersPaginationResponse";
/* eslint-disable import/export */
export abstract class GetProjectBOUsersRPC implements
  LeoRPC<GetProjectBOUsersRPC.Request, GetProjectBOUsersRPC.Response, GetProjectBOUsersRPC.Errors.Errors>
{
  abstract execute(
    request: GetProjectBOUsersRPC.Request
  ): Promise<LeoRPCResult<GetProjectBOUsersRPC.Response, GetProjectBOUsersRPC.Errors.Errors>>;
}

export namespace GetProjectBOUsersRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    sortList: GetProjectBOUsersSortOrderDTO[];
    name?: string | null;
    emailAddress?: string | null;
    projectId?: string | null;
    role?: string | null;
    addedOnStartDate?: string | null;
    addedOnEndDate?: string | null;
    itemsPerPage: ProjectItemsPerPageDTO;
    pageIndex: ProjectPageIndexDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetProjectBOUsersRPCRequest: boolean = true;
  
    readonly sortList: GetProjectBOUsersSortOrder[];
    readonly name: string | null;
    readonly emailAddress: string | null;
    readonly projectId: string | null;
    readonly role: string | null;
    readonly addedOnStartDate: LeoDate | null;
    readonly addedOnEndDate: LeoDate | null;
    readonly itemsPerPage: ProjectItemsPerPage;
    readonly pageIndex: ProjectPageIndex;
  
    static readonly constraints = {
      "name": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "emailAddress": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "projectId": {
        "minSize": 3,
        "maxSize": 200,
        
        
        
        
      },
      "role": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
    }
  
    constructor(
    sortList: GetProjectBOUsersSortOrder[], 
    name: string | null = null, 
    emailAddress: string | null = null, 
    projectId: string | null = null, 
    role: string | null = null, 
    addedOnStartDate: LeoDate | null = null, 
    addedOnEndDate: LeoDate | null = null, 
    itemsPerPage: ProjectItemsPerPage, 
    pageIndex: ProjectPageIndex
    ) {
      this.sortList = sortList;
      if ( name !== undefined && name !== null) {
        Request.validateName(name);
      }
      this.name = name;
      if ( emailAddress !== undefined && emailAddress !== null) {
        Request.validateEmailAddress(emailAddress);
      }
      this.emailAddress = emailAddress;
      if ( projectId !== undefined && projectId !== null) {
        Request.validateProjectId(projectId);
      }
      this.projectId = projectId;
      if ( role !== undefined && role !== null) {
        Request.validateRole(role);
      }
      this.role = role;
      this.addedOnStartDate = addedOnStartDate;
      this.addedOnEndDate = addedOnEndDate;
      this.itemsPerPage = itemsPerPage;
      this.pageIndex = pageIndex;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const sortList = getList<GetProjectBOUsersSortOrderDTO, GetProjectBOUsersSortOrder>(
        dto,
        "sortList",
        GetProjectBOUsersSortOrder.fromDTO,
        InvalidRequestSortListError
      );
      const name = getOptionalString(dto, "name", InvalidRequestNameError);
      const emailAddress = getOptionalString(dto, "emailAddress", InvalidRequestEmailAddressError);
      const projectId = getOptionalString(dto, "projectId", InvalidRequestProjectIdError);
      const role = getOptionalString(dto, "role", InvalidRequestRoleError);
      const addedOnStartDate = getOptionalLeoDate(dto, "addedOnStartDate", InvalidRequestAddedOnStartDateError);
      const addedOnEndDate = getOptionalLeoDate(dto, "addedOnEndDate", InvalidRequestAddedOnEndDateError);
      const itemsPerPage = ProjectItemsPerPage.fromDTO(getObject(dto, "itemsPerPage", InvalidRequestItemsPerPageError));
      const pageIndex = ProjectPageIndex.fromDTO(getObject(dto, "pageIndex", InvalidRequestPageIndexError));
      return new Request(
        sortList,
        name,
        emailAddress,
        projectId,
        role,
        addedOnStartDate,
        addedOnEndDate,
        itemsPerPage,
        pageIndex
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          sortList: this.sortList.map((e: GetProjectBOUsersSortOrder) => { return e.toDTO() }),
          itemsPerPage: this.itemsPerPage.toDTO(),
          pageIndex: this.pageIndex.toDTO()
      };
      if (this.name !== undefined && this.name !== null){
        dto.name = this.name;
      }
      if (this.emailAddress !== undefined && this.emailAddress !== null){
        dto.emailAddress = this.emailAddress;
      }
      if (this.projectId !== undefined && this.projectId !== null){
        dto.projectId = this.projectId;
      }
      if (this.role !== undefined && this.role !== null){
        dto.role = this.role;
      }
      if (this.addedOnStartDate !== undefined && this.addedOnStartDate !== null){
        dto.addedOnStartDate = this.addedOnStartDate.toDTO();
      }
      if (this.addedOnEndDate !== undefined && this.addedOnEndDate !== null){
        dto.addedOnEndDate = this.addedOnEndDate.toDTO();
      }
      return dto;
    }
    private static validateName = (name: string): void => {
      if(name.length < 1) {
        throw new InvalidRequestNameMinSizeError(
          1,
          `Attribute name is too short. Size is ${name.length}. Min size is 1.`
        );
      }
      if(name.length > 200) {
        throw new InvalidRequestNameMaxSizeError(
          200,
          `Attribute name is too long. Size is ${name.length}. Max size is 200.`
        );
      }
        if(name.trim().length === 0) {
          throw new InvalidRequestNameNonEmptyError(
            `Attribute name cannot be an empty string.`
          );
        }
    }
    private static validateEmailAddress = (emailAddress: string): void => {
      if(emailAddress.length < 1) {
        throw new InvalidRequestEmailAddressMinSizeError(
          1,
          `Attribute emailAddress is too short. Size is ${emailAddress.length}. Min size is 1.`
        );
      }
      if(emailAddress.length > 200) {
        throw new InvalidRequestEmailAddressMaxSizeError(
          200,
          `Attribute emailAddress is too long. Size is ${emailAddress.length}. Max size is 200.`
        );
      }
        if(emailAddress.trim().length === 0) {
          throw new InvalidRequestEmailAddressNonEmptyError(
            `Attribute emailAddress cannot be an empty string.`
          );
        }
    }
    private static validateProjectId = (projectId: string): void => {
      if(projectId.length < 3) {
        throw new InvalidRequestProjectIdMinSizeError(
          3,
          `Attribute projectId is too short. Size is ${projectId.length}. Min size is 3.`
        );
      }
      if(projectId.length > 200) {
        throw new InvalidRequestProjectIdMaxSizeError(
          200,
          `Attribute projectId is too long. Size is ${projectId.length}. Max size is 200.`
        );
      }
        if(projectId.trim().length === 0) {
          throw new InvalidRequestProjectIdNonEmptyError(
            `Attribute projectId cannot be an empty string.`
          );
        }
    }
    private static validateRole = (role: string): void => {
      if(role.length < 1) {
        throw new InvalidRequestRoleMinSizeError(
          1,
          `Attribute role is too short. Size is ${role.length}. Min size is 1.`
        );
      }
      if(role.length > 200) {
        throw new InvalidRequestRoleMaxSizeError(
          200,
          `Attribute role is too long. Size is ${role.length}. Max size is 200.`
        );
      }
        if(role.trim().length === 0) {
          throw new InvalidRequestRoleNonEmptyError(
            `Attribute role cannot be an empty string.`
          );
        }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetProjectBOUsersRPC(sortList='${this.sortList}',name='${this.name}',emailAddress='${this.emailAddress}',projectId='${this.projectId}',role='${this.role}',addedOnStartDate='${this.addedOnStartDate}',addedOnEndDate='${this.addedOnEndDate}',itemsPerPage='${this.itemsPerPage}',pageIndex='${this.pageIndex}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    getProjectBOUsersPaginationResponse: GetProjectBOUsersPaginationResponseDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetProjectBOUsersResponse: boolean = true;
  
    readonly getProjectBOUsersPaginationResponse: GetProjectBOUsersPaginationResponse[];
    readonly totalItems: number;
  
  
    constructor(
    getProjectBOUsersPaginationResponse: GetProjectBOUsersPaginationResponse[], 
    totalItems: number
    ) {
      this.getProjectBOUsersPaginationResponse = getProjectBOUsersPaginationResponse;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const getProjectBOUsersPaginationResponse = getList<GetProjectBOUsersPaginationResponseDTO, GetProjectBOUsersPaginationResponse>(
        dto,
        "getProjectBOUsersPaginationResponse",
        GetProjectBOUsersPaginationResponse.fromDTO,
        InvalidResponseGetProjectBoUsersPaginationResponseError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        getProjectBOUsersPaginationResponse,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          getProjectBOUsersPaginationResponse: this.getProjectBOUsersPaginationResponse.map((e: GetProjectBOUsersPaginationResponse) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetProjectBOUsers(getProjectBOUsersPaginationResponse='${this.getProjectBOUsersPaginationResponse}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidDateRange = "INVALID_DATE_RANGE"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetProjectBOUsersRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidDateRangeDTO {
      code: string;
    }
    
    export class InvalidDateRange extends LeoRPCError<RPCError> {
      readonly isInvalidDateRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidDateRange;
        super(code);
      }
    
    
      toDTO(): InvalidDateRangeDTO {
        const dto: InvalidDateRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidDateRange.prototype.toString = function toString(): string {
      return `GetProjectBOUsersRPC.INVALID_DATE_RANGE()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidDateRange;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidDateRangeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidDateRange:
          return new InvalidDateRange();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSortListError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortListError";
  }
}

export class InvalidRequestNameError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameError";
  }
}

export class InvalidRequestNameMaxSizeError extends InvalidRequestNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameMaxSizeError";
  }
}

export class InvalidRequestNameMinSizeError extends InvalidRequestNameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameMinSizeError";
  }
}

export class InvalidRequestNameNonEmptyError extends InvalidRequestNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameNonEmptyError";
  }
}

export class InvalidRequestEmailAddressError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressError";
  }
}

export class InvalidRequestEmailAddressMaxSizeError extends InvalidRequestEmailAddressError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMaxSizeError";
  }
}

export class InvalidRequestEmailAddressMinSizeError extends InvalidRequestEmailAddressError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMinSizeError";
  }
}

export class InvalidRequestEmailAddressNonEmptyError extends InvalidRequestEmailAddressError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressNonEmptyError";
  }
}

export class InvalidRequestProjectIdError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestProjectIdMaxSizeError extends InvalidRequestProjectIdError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdMaxSizeError";
  }
}

export class InvalidRequestProjectIdMinSizeError extends InvalidRequestProjectIdError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdMinSizeError";
  }
}

export class InvalidRequestProjectIdNonEmptyError extends InvalidRequestProjectIdError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdNonEmptyError";
  }
}

export class InvalidRequestRoleError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleError";
  }
}

export class InvalidRequestRoleMaxSizeError extends InvalidRequestRoleError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleMaxSizeError";
  }
}

export class InvalidRequestRoleMinSizeError extends InvalidRequestRoleError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleMinSizeError";
  }
}

export class InvalidRequestRoleNonEmptyError extends InvalidRequestRoleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleNonEmptyError";
  }
}

export class InvalidRequestAddedOnStartDateError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnStartDateError";
  }
}

export class InvalidRequestAddedOnEndDateError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnEndDateError";
  }
}

export class InvalidRequestItemsPerPageError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestPageIndexError extends GetProjectBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidResponseGetProjectBoUsersPaginationResponseError extends GetProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseGetProjectBoUsersPaginationResponseError";
  }
}

export class InvalidResponseTotalItemsError extends GetProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidDateRangeError extends GetProjectBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidDateRangeError";
  }
}

